<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="row align-items-center p-t-40 p-b-40">
            <div class="col-md-8 text-white">
              <h2 class="page-title"><i class="fe fe-repeat mr-2"></i>Change of Provider</h2>
              <p class="page-subtitle">Request a transfer to a new health facility</p>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <form @submit.prevent="makeRequest">
          <div class="card form-card shadow">

            <div class="form-card-header">
              <div>
                <h5 class="mb-0 font-weight-bold text-white">Change of Provider Request</h5>
                <p class="mb-0 mt-1 page-subtitle" style="opacity:.8; font-size:.85rem;">
                  Fill in the details below to process a facility transfer
                </p>
              </div>
              <button class="form-close-btn" type="button" @click="clearIt">
                <i class="fe fe-refresh-cw"></i>
              </button>
            </div>

            <div class="card-body px-4 pb-4">

              <!-- Step 1: Find Enrollee -->
              <div class="form-section">
                <div class="form-section-title">
                  <span class="section-number">1</span> Find Enrollee
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label class="form-label">Search Enrollee</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white"><i class="fe fe-search text-muted"></i></span>
                      </div>
                      <input type="text" class="form-control border-left-0"
                        placeholder="First Name, NIN, Phone, ID…"
                        v-model="searchkey"
                        @change="searchIDCard" />
                    </div>
                  </div>
                  <div class="form-group col-md-6">
                    <label class="form-label">
                      Select Enrollee
                      <span class="text-muted font-weight-normal ml-1" style="font-size: .8rem;" v-if="enrollees && enrollees.length">
                        — {{ enrollees.length }} result(s)
                      </span>
                    </label>
                    <select class="form-control styled-select" v-model="enrollee_details" required>
                      <option value="">-- Select from results --</option>
                      <option :value="user" v-for="user in enrollees" :key="user.id">
                        {{ user.user_query }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Patient Preview -->
                <div class="patient-preview" v-if="enrollee_details && enrollee_details.firstname">
                  <img
                    :src="`https://claimify-api.hayokmedicare.ng/image/${enrollee_details.user_image}`"
                    class="preview-avatar"
                    onerror="this.onerror=null; this.src='/assets/img/kgshia_logo.png'"
                    alt="Patient Photo"
                  />
                  <div class="flex-grow-1">
                    <strong style="font-size: 1rem;">{{ enrollee_details.firstname }} {{ enrollee_details.lastname }}</strong>
                    <div class="d-flex flex-wrap mt-1" style="gap: 16px;">
                      <small class="text-muted" v-if="enrollee_details.id_card_number">
                        <i class="fe fe-credit-card mr-1"></i>{{ enrollee_details.id_card_number }}
                      </small>
                      <small class="text-muted" v-if="enrollee_details.phone_number">
                        <i class="fe fe-phone mr-1"></i>{{ enrollee_details.phone_number }}
                      </small>
                      <small class="text-muted" v-if="enrollee_details.userprovider">
                        <i class="fe fe-home mr-1"></i>{{ enrollee_details.userprovider.agency_name }}
                      </small>
                    </div>
                  </div>
                  <span class="badge badge-success">Active</span>
                </div>
              </div>

              <!-- Step 2: Reason for Change -->
              <div class="form-section">
                <div class="form-section-title">
                  <span class="section-number">2</span> Reason for Change
                </div>
                <div class="mb-3">
                  <p class="small text-muted mb-2">Quick select a reason:</p>
                  <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                    <button
                      type="button"
                      @click="selectSuggestion(reason)"
                      v-for="reason in reasons"
                      :key="reason"
                      :class="['reason-chip', transfer.reason_for_change === reason ? 'reason-chip--active' : '']">
                      {{ reason }}
                    </button>
                  </div>
                </div>
                <div class="form-group mb-0">
                  <label class="form-label">Reason <span class="text-danger">*</span></label>
                  <textarea
                    rows="4"
                    class="form-control styled-textarea"
                    v-model="transfer.reason_for_change"
                    placeholder="Describe the reason for this facility change…"
                    required>
                  </textarea>
                </div>
              </div>

              <!-- Step 3: Facility Change -->
              <div class="form-section" v-if="enrollee_details && enrollee_details.firstname">
                <div class="form-section-title">
                  <span class="section-number">3</span> Facility Transfer
                </div>
                <div class="facility-transfer-row">
                  <!-- From -->
                  <div class="facility-box facility-box--from flex-grow-1">
                    <p class="facility-box__label"><i class="fe fe-arrow-up-right mr-1"></i>Current Facility</p>
                    <p class="facility-box__value" v-if="enrollee_details.userprovider">
                      {{ enrollee_details.userprovider.agency_name }}
                    </p>
                    <p class="facility-box__value text-muted" v-else>Not assigned</p>
                  </div>

                  <div class="transfer-arrow">
                    <i class="fe fe-arrow-right"></i>
                  </div>

                  <!-- To -->
                  <div class="facility-box facility-box--to flex-grow-1">
                    <p class="facility-box__label"><i class="fe fe-arrow-down-left mr-1"></i>New Facility <span class="text-danger">*</span></p>
                    <v-select
                      v-model="transfer.new_health_facility"
                      :options="providers"
                      label="agency_name"
                      :value="transfer.new_health_facility"
                      @input="selected"
                      placeholder="Search new facility…"
                    />
                  </div>
                </div>
              </div>

              <!-- Step 4: Upload Form -->
              <div class="form-section">
                <div class="form-section-title">
                  <span class="section-number">4</span> Supporting Document
                </div>
                <div class="upload-zone">
                  <label class="upload-label" for="doc-upload">
                    <i class="fe fe-upload-cloud" style="font-size: 1.6rem; margin-bottom: 6px; display: block;"></i>
                    <span v-if="document">
                      <strong>{{ document.name }}</strong><br>
                      <small class="text-muted">Click to change</small>
                    </span>
                    <span v-else>
                      <strong>Click to upload</strong> or drag and drop<br>
                      <small class="text-muted">Change of Provider form (PDF, JPG, PNG)</small>
                    </span>
                  </label>
                  <input id="doc-upload" type="file" class="d-none" @change="attachDoc" />
                </div>
              </div>

              <!-- Actions -->
              <div class="d-flex justify-content-end" style="gap: 10px; margin-top: 8px;">
                <button type="button" class="btn btn-cancel" @click="clearIt">
                  <i class="fe fe-x mr-1"></i> Clear
                </button>

                <button type="submit" class="btn btn-create" :disabled="isLoading">
                  <span v-if="isLoading"><i class="mdi mdi-loading mdi-spin mr-1"></i>Submitting…</span>
                  <span v-else><i class="fe fe-send mr-1"></i>Submit Request</span>
                </button>
              </div>

            </div>
          </div>
        </form>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: { Navbar, Loading },
  data() {
    return {
      user: null,
      enrollees: [],
      reasons: [
        "Change of location",
        "Poor service delivery",
        "Extortion / Over-billing",
        "Facility Disaccreditation",
        "Proximity to new facility",
      ],
      search_result: "",
      enrollee_details: "",
      searchkey: "",
      transfers: "",
      quickref: "",
      document: null,
      providers: [],
      isLoading: false,
      fullPage: true,
      transfer: {
        reason_for_change: "",
        client_id: "",
        new_health_facility: "",
        previous_health_facility: 0,
        status: "",
        prepared_by: "",
      },
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  methods: {
    selectSuggestion(reason) {
      this.transfer.reason_for_change = reason;
    },

    selected(value) {
      this.transfer.new_health_facility = value;
    },

    clearIt() {
      this.transfer.reason_for_change = "";
      this.transfer.new_health_facility = "";
      this.transfer.previous_health_facility = "";
      this.transfer.status = "";
      this.transfer.prepared_by = "";
      this.searchkey = "";
      this.enrollee_details = "";
      this.document = null;
    },

    searchIDCard() {
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/searchenrollees`, {
          agency_id: 90,
          request_query: this.searchkey,
        })
        .then((response) => {
          this.enrollees = response.data;
          this.$toasted.info("Search complete", { position: "top-center", duration: 2000 });
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
          this.$toasted.error("Enrollee not found", { position: "top-center", duration: 3000 });
        });
    },

    getProviders() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/providerAgency/90`)
        .then((response) => { this.providers = response.data.data; })
        .catch((error) => { console.error(error); });
    },

    AllTransfers() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/all/change_providers/90`)
        .then((response) => { this.transfers = response.data; })
        .catch((error) => { console.error(error); });
    },

    attachDoc(event) {
      this.document = event.target.files[0];
    },

    makeRequest() {
      this.isLoading = true;
      this.user = JSON.parse(localStorage.getItem("user"));
      var formData = new FormData();
      if (this.document) formData.append("claim_document", this.document);
      formData.append("type", "cof");
      formData.append("client_id", this.enrollee_details.id);
      formData.append("prepared_by", this.user.id);
      formData.append("agency_id", 90);
      formData.append("new_health_facility", this.transfer.new_health_facility.id);
      formData.append(
        "previous_health_facility",
        this.enrollee_details.provider_id == null ? 0 : this.enrollee_details.provider_id
      );
      formData.append("status", "created");
      formData.append("reason_for_change", this.transfer.reason_for_change);

      this.axios
        .post("/api/v1/auth/change_providers", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(() => {
          this.isLoading = false;
          this.$toasted.success("Request submitted successfully!", { position: "top-center", duration: 3000 });
          this.clearIt();
          this.AllTransfers();
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
          this.$toasted.error("Error submitting request. Try again.", { position: "top-center", duration: 3000 });
        });
    },
  },
  created() {
    this.AllTransfers();
    this.getProviders();
  },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
}
.page-title { font-size: 1.6rem; font-weight: 700; margin: 0; color: #fff; }
.page-subtitle { opacity: .75; margin: 4px 0 0; font-size: .9rem; }
.mt-n3 { margin-top: -16px; }

/* ── Form Card ── */
.form-card { border-radius: 14px; overflow: hidden; border: none; }
.form-card-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 28px;
  background: linear-gradient(135deg, #1e3a5f, #2563eb);
}
.form-close-btn {
  background: rgba(255,255,255,.15); border: none; border-radius: 8px;
  color: #fff; width: 34px; height: 34px; cursor: pointer; font-size: 1rem;
  display: flex; align-items: center; justify-content: center;
}
.form-close-btn:hover { background: rgba(255,255,255,.28); }

/* ── Form Sections ── */
.form-section { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #f1f5f9; }
.form-section:last-of-type { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.form-section-title {
  display: flex; align-items: center; gap: 10px;
  font-weight: 600; color: #374151; font-size: .9rem; margin-bottom: 16px;
}
.section-number {
  width: 26px; height: 26px; border-radius: 50%;
  background: linear-gradient(135deg, #1e3a5f, #2563eb); color: #fff;
  font-size: .75rem; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.form-label { font-weight: 600; font-size: .85rem; color: #374151; margin-bottom: 6px; }
.styled-select {
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  padding: 9px 12px; font-size: .88rem;
}
.styled-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }
.styled-textarea {
  border: 1.5px solid #e2e8f0; border-radius: 8px; resize: vertical; font-size: .88rem;
}
.styled-textarea:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }

/* Patient preview */
.patient-preview {
  display: flex; align-items: center; gap: 14px;
  background: #f0fdf4; border: 1px solid #bbf7d0;
  border-radius: 10px; padding: 14px 18px; margin-top: 10px;
}
.preview-avatar {
  width: 56px; height: 56px; border-radius: 50%;
  object-fit: cover; border: 2px solid #10b981; flex-shrink: 0;
}

/* Reason chips */
.reason-chip {
  padding: 5px 14px; border-radius: 20px; font-size: .8rem; font-weight: 500; cursor: pointer;
  background: #f1f5f9; border: 1.5px solid #e2e8f0; color: #374151;
  transition: all .15s ease;
}
.reason-chip:hover { background: #dbeafe; border-color: #93c5fd; color: #1d4ed8; }
.reason-chip--active { background: #2563eb; border-color: #2563eb; color: #fff; }

/* Facility transfer row */
.facility-transfer-row {
  display: flex; align-items: center; gap: 16px;
  flex-wrap: wrap;
}
.facility-box {
  border-radius: 10px; padding: 14px 18px; min-width: 200px;
}
.facility-box--from { background: #fef3c7; border: 1px solid #fcd34d; }
.facility-box--to   { background: #f0fdf4; border: 1px solid #bbf7d0; }
.facility-box__label {
  font-size: .72rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .05em; color: #6b7280; margin-bottom: 6px;
}
.facility-box__value { font-weight: 600; color: #374151; margin: 0; font-size: .9rem; }
.transfer-arrow {
  width: 36px; height: 36px; border-radius: 50%;
  background: #e0e7ff; color: #3730a3;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; flex-shrink: 0;
}

/* Upload zone */
.upload-zone {
  border: 2px dashed #cbd5e1; border-radius: 10px; background: #f8fafc;
  transition: border-color .2s;
}
.upload-zone:hover { border-color: #2563eb; }
.upload-label {
  display: block; text-align: center; padding: 28px 24px;
  cursor: pointer; color: #475569; margin: 0;
}

/* Buttons */
.btn-create {
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  color: #fff; border: none; border-radius: 8px; padding: 10px 24px; font-weight: 600;
}
.btn-create:hover { opacity: .9; color: #fff; }
.btn-cancel {
  background: #f1f5f9; border: none; border-radius: 8px;
  padding: 10px 20px; color: #475569; font-weight: 600;
}
</style>
